import { useEffect, useState } from "react";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { post } from "@common/api/apiShared";
import { getHeaderCheckBoxState, MAX_BULK_SELECTION, setSelectedOnPage, toggleSelected } from "@common/bulkSelection/bulkSelection.utils";

export interface UseActivitiesBatchDeleteProps {
    onBatchDeleteSuccess: () => void;
    /** Changes whenever the rows the list shows change other than by paging, which clears the selection. */
    rowFilterKey: string;
}

export function useActivitiesBatchDelete(props: UseActivitiesBatchDeleteProps) {
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [hardDelete, setHardDelete] = useState(false);
    const [showBatchDeleteDialog, setShowBatchDeleteDialog] = useState(false);

    const batchDeleteMutation = useDotLegalMutation((body: { annualWheelActivityIds: Array<string>; hardDelete: boolean }) =>
        post<string>(`/annualWheelActivity/batch-delete`, body)
    );

    function clearSelection() {
        setSelectedIds(new Set());
        setHardDelete(false);
    }

    useEffect(() => {
        setSelectedIds(new Set());
        setHardDelete(false);
    }, [props.rowFilterKey]);

    function exitSelectionMode() {
        setIsSelectionMode(false);
        clearSelection();
    }

    function onCheckboxClick(id: string) {
        setSelectedIds((prev) => toggleSelected(prev, id));
    }

    function onSelectAllOnPage(pageIds: Array<string>, checked: boolean) {
        setSelectedIds((prev) => setSelectedOnPage(prev, pageIds, checked));
    }

    const selectedCount = selectedIds.size;
    const exceedsLimit = selectedCount > MAX_BULK_SELECTION;
    const canDelete = selectedCount > 0 && !exceedsLimit;

    function onBatchDeleteConfirm() {
        batchDeleteMutation.mutateAsync(
            { annualWheelActivityIds: Array.from(selectedIds), hardDelete },
            {
                onSuccess: () => {
                    setShowBatchDeleteDialog(false);
                    clearSelection();
                    props.onBatchDeleteSuccess();
                },
            }
        );
    }

    return {
        isSelectionMode,
        setIsSelectionMode,
        selectedIds,
        selectedCount,
        exceedsLimit,
        canDelete,
        hardDelete,
        setHardDelete,
        showBatchDeleteDialog,
        setShowBatchDeleteDialog,
        exitSelectionMode,
        clearSelection,
        onCheckboxClick,
        onSelectAllOnPage,
        getHeaderCheckBoxState: (pageIds: Array<string>) => getHeaderCheckBoxState(selectedIds, pageIds),
        onBatchDeleteConfirm,
        isBatchDeleting: batchDeleteMutation.isLoading,
    };
}
